import React, { useState } from "react";
import Layout from "../../../Layouts/Layout";
import { Link } from "@inertiajs/inertia-react";
import { Inertia } from "@inertiajs/inertia";
import Select from "react-select";
import { SelectStyles } from "../../../styles/ReactSelect";
import {
  MapPinIcon,
  WrenchScrewdriverIcon,
  MagnifyingGlassIcon,
  PlusCircleIcon,
  PhotoIcon,
} from "@heroicons/react/24/outline";

// Tradesman-side browse. The server only ever returns requests inside the
// tradesman's visibility set (their districts x their trades); the filters
// below narrow within that set and the dropdown options are limited to the
// tradesman's own trades and districts.
const Browse = ({ requests, won_requests, profile_summary, filters, pagination }) => {
  const [search, setSearch] = useState(filters.q || "");

  const tradeOptions = profile_summary.trades.map((t) => ({
    value: String(t.id),
    label: t.name,
  }));
  const districtOptions = profile_summary.postcodes.map((d) => ({
    value: String(d.id),
    label: d.code,
  }));
  const sortOptions = [
    { value: "newest", label: "Newest first" },
    { value: "oldest", label: "Oldest first" },
    { value: "start_date", label: "Soonest ideal start" },
  ];

  function applyFilters(overrides) {
    const next = {
      trade_id: filters.trade_id,
      postcode_district_id: filters.postcode_district_id,
      q: search.trim() || null,
      sort: filters.sort,
      ...overrides,
    };
    const query = {};
    Object.keys(next).forEach((key) => {
      if (next[key]) query[key] = next[key];
    });
    Inertia.get("/domestic/tradesman_requests", query, {
      preserveState: true,
      preserveScroll: true,
    });
  }

  function submitSearch(e) {
    e.preventDefault();
    applyFilters({});
  }

  const hasActiveFilters =
    filters.trade_id || filters.postcode_district_id || filters.q;

  return (
    <Layout
      title="Domestic Requests"
      secondary_button={{ name: "Edit profile", link: "/domestic/tradesman_profile/edit" }}
    >
      <div className="mt-2 space-y-6">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <div className="rounded-lg border border-gray-200 bg-white px-5 py-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-orange-50 p-2">
                <MapPinIcon className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Districts covered
                </p>
                <p className="mt-0.5 text-2xl font-semibold text-gray-900">
                  {profile_summary.postcodes.length}
                </p>
              </div>
            </div>
            <p className="mt-3 text-sm text-gray-600">
              {profile_summary.postcodes.map((d) => d.code).join(", ") ||
                "None set"}
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white px-5 py-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-orange-50 p-2">
                <WrenchScrewdriverIcon className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Trades covered
                </p>
                <p className="mt-0.5 text-2xl font-semibold text-gray-900">
                  {profile_summary.trades.length}
                </p>
              </div>
            </div>
            <p className="mt-3 text-sm text-gray-600">
              {profile_summary.trades.map((t) => t.name).join(", ") ||
                "None set"}
            </p>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white px-5 py-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="rounded-md bg-orange-50 p-2">
                <MagnifyingGlassIcon className="h-5 w-5 text-orange-600" />
              </div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                  Open requests
                </p>
                <p className="mt-0.5 text-2xl font-semibold text-gray-900">
                  {pagination.total}
                </p>
              </div>
            </div>
            <p className="mt-3 text-sm text-gray-600">
              Matching your districts and trades
              {hasActiveFilters ? " (with filters applied)" : ""}
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
          <p className="text-sm text-gray-600">
            Showing open requests in{" "}
            <span className="font-medium text-gray-900">
              {profile_summary.postcodes.map((d) => d.code).join(", ")}
            </span>{" "}
            for{" "}
            <span className="font-medium text-gray-900">
              {profile_summary.trades.map((t) => t.name).join(", ")}
            </span>
          </p>

          <div className="mt-5 grid grid-cols-1 gap-3 lg:grid-cols-4">
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">
                Trade
              </label>
              <Select
                styles={SelectStyles}
                options={tradeOptions}
                isClearable
                placeholder="All my trades"
                value={
                  tradeOptions.find((o) => o.value === filters.trade_id) || null
                }
                onChange={(selected) =>
                  applyFilters({ trade_id: selected ? selected.value : null })
                }
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">
                Postcode district
              </label>
              <Select
                styles={SelectStyles}
                options={districtOptions}
                isClearable
                placeholder="All my districts"
                value={
                  districtOptions.find(
                    (o) => o.value === filters.postcode_district_id
                  ) || null
                }
                onChange={(selected) =>
                  applyFilters({
                    postcode_district_id: selected ? selected.value : null,
                  })
                }
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-500">
                Sort by
              </label>
              <Select
                styles={SelectStyles}
                options={sortOptions}
                value={sortOptions.find((o) => o.value === filters.sort)}
                onChange={(selected) => applyFilters({ sort: selected.value })}
              />
            </div>
            <form onSubmit={submitSearch} className="flex items-end gap-2">
              <div className="w-full">
                <label className="mb-1 block text-xs font-medium text-gray-500">
                  Search
                </label>
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search request descriptions…"
                  className="block w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-orange-500 focus:outline-none focus:ring-orange-500 sm:text-sm"
                />
              </div>
              <button
                type="submit"
                className="rounded-md bg-orange-600 py-2 px-4 text-sm font-medium text-white hover:bg-orange-700"
              >
                Search
              </button>
              {hasActiveFilters && (
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    Inertia.get("/domestic/tradesman_requests");
                  }}
                  className="rounded-md border border-gray-300 py-2 px-4 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Clear
                </button>
              )}
            </form>
          </div>
        </div>

        {requests.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white px-6 py-16 text-center shadow-sm">
            <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-orange-50">
              <MagnifyingGlassIcon className="h-6 w-6 text-orange-600" />
            </div>
            <h3 className="mt-4 text-base font-semibold text-gray-900">
              {hasActiveFilters
                ? "No requests match these filters"
                : "No open requests in your districts yet"}
            </h3>
            <p className="mx-auto mt-2 max-w-lg text-sm text-gray-500">
              {hasActiveFilters
                ? "Try clearing filters or choosing a different trade or district."
                : "Customer requests appear here when they match your trades and postcode districts. Add more districts to widen your reach."}
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-3 sm:flex-row">
              {hasActiveFilters ? (
                <button
                  type="button"
                  onClick={() => {
                    setSearch("");
                    Inertia.get("/domestic/tradesman_requests");
                  }}
                  className="inline-flex items-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50"
                >
                  Clear filters
                </button>
              ) : null}
              <Link
                href="/domestic/tradesman_profile/edit"
                className="inline-flex items-center gap-2 rounded-md bg-orange-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-orange-700"
              >
                <PlusCircleIcon className="h-5 w-5" />
                Add more districts
              </Link>
            </div>
          </div>
        ) : (
          <ul className="grid grid-cols-1 gap-4 lg:grid-cols-2">
            {requests.map((r) => (
              <li key={r.id}>
                <Link
                  href={`/domestic/tradesman_requests/${r.id}`}
                  className="block h-full rounded-lg border border-gray-200 bg-white px-5 py-4 shadow-sm transition hover:border-orange-300 hover:bg-orange-50/40"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-base font-semibold text-gray-900">
                        {r.trades.join(", ")}
                      </p>
                      <p className="mt-1 flex items-center text-sm text-gray-500">
                        <MapPinIcon className="mr-1.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                        {r.postcode_district_code}
                        {r.images && r.images.length > 0 && (
                          <span className="ml-3 flex items-center text-gray-400">
                            <PhotoIcon className="mr-1 h-4 w-4 flex-shrink-0" />
                            {r.images.length}
                          </span>
                        )}
                      </p>
                    </div>
                    {r.responded && (
                      <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-0.5 text-xs font-semibold leading-5 text-blue-800">
                        Responded
                      </span>
                    )}
                  </div>
                  <p className="mt-3 text-sm text-gray-600">
                    {r.description.length > 180
                      ? `${r.description.slice(0, 180)}…`
                      : r.description}
                  </p>
                  <p className="mt-4 text-xs text-gray-400">
                    Posted {r.created_at}
                    {r.ideal_start_date
                      ? ` · Ideal start ${r.ideal_start_date}`
                      : ""}
                  </p>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-between rounded-lg border border-gray-200 bg-white px-4 py-3 shadow-sm">
            <button
              type="button"
              disabled={pagination.page <= 1}
              onClick={() => applyFilters({ page: pagination.page - 1 })}
              className="rounded-md border border-gray-300 py-1.5 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              &larr; Previous
            </button>
            <span className="text-sm text-gray-500">
              Page {pagination.page} of {pagination.total_pages}
            </span>
            <button
              type="button"
              disabled={pagination.page >= pagination.total_pages}
              onClick={() => applyFilters({ page: pagination.page + 1 })}
              className="rounded-md border border-gray-300 py-1.5 px-3 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-40"
            >
              Next &rarr;
            </button>
          </div>
        )}

        {won_requests && won_requests.length > 0 && (
          <div>
            <h2 className="text-base font-semibold text-gray-900">
              Jobs you&apos;ve won
              <span className="ml-2 text-sm font-normal text-gray-400">
                ({won_requests.length})
              </span>
            </h2>
            <p className="mt-1 text-sm text-gray-500">
              Requests a homeowner accepted you for — these stay here
              even if they&apos;re outside your current districts or trades.
            </p>
            <ul className="mt-3 grid grid-cols-1 gap-4 lg:grid-cols-2">
              {won_requests.map((r) => (
                <li key={r.id}>
                  <Link
                    href={`/domestic/tradesman_requests/${r.id}`}
                    className="block h-full rounded-lg border border-gray-200 bg-white px-5 py-4 shadow-sm transition hover:border-orange-300 hover:bg-orange-50/40"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="text-base font-semibold text-gray-900">
                          {r.trades.join(", ")}
                        </p>
                        <p className="mt-1 flex items-center text-sm text-gray-500">
                          <MapPinIcon className="mr-1.5 h-4 w-4 flex-shrink-0 text-gray-400" />
                          {r.postcode_district_code}
                          {r.images && r.images.length > 0 && (
                            <span className="ml-3 flex items-center text-gray-400">
                              <PhotoIcon className="mr-1 h-4 w-4 flex-shrink-0" />
                              {r.images.length}
                            </span>
                          )}
                        </p>
                      </div>
                      <span className="inline-flex rounded-full bg-green-100 px-2.5 py-0.5 text-xs font-semibold leading-5 text-green-800">
                        Won
                      </span>
                    </div>
                    <p className="mt-3 text-sm text-gray-600">
                      {r.description.length > 180
                        ? `${r.description.slice(0, 180)}…`
                        : r.description}
                    </p>
                    <p className="mt-4 text-xs text-gray-400">
                      Posted {r.created_at}
                      {r.ideal_start_date
                        ? ` · Ideal start ${r.ideal_start_date}`
                        : ""}
                    </p>
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Browse;
